import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Loader2, RefreshCw, CalendarDays, AlertTriangle, Plus, X, Trash2,
  Sparkles, ChevronDown, Info, GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLE = {
  work:          { bg: "bg-white",         text: "text-zinc-900",  ring: "border-zinc-300",        label: "Cours" },
  weekend:       { bg: "bg-zinc-100",      text: "text-zinc-400",  ring: "border-zinc-200",        label: "Week-end" },
  holiday:       { bg: "bg-red-100",       text: "text-red-900",   ring: "border-red-300",         label: "Férié" },
  vacation:      { bg: "bg-amber-100",     text: "text-amber-900", ring: "border-amber-300",       label: "Vacances" },
  override_in:   { bg: "bg-emerald-500",   text: "text-white",     ring: "border-emerald-700",     label: "Rattrapage" },
  override_out:  { bg: "bg-fuchsia-500",   text: "text-white",     ring: "border-fuchsia-700",     label: "Fermé" },
};

const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const WEEKDAYS_FR = ["L", "M", "M", "J", "V", "S", "D"];

function ymKey(iso) { return iso.slice(0, 7); }

function MonthGrid({ year, ym, days, overridesByDate, onCellClick }) {
  const monthIdx = parseInt(ym.split("-")[1], 10) - 1;
  const monthYear = parseInt(ym.split("-")[0], 10);
  // First weekday alignment (Mon=0..Sun=6)
  const first = new Date(Date.UTC(monthYear, monthIdx, 1));
  const firstWd = (first.getUTCDay() + 6) % 7; // shift Sun=0 to last
  const filtered = days.filter((d) => d.date.startsWith(ym));
  if (filtered.length === 0) return null;
  return (
    <div className="bg-white border-2 border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]" data-testid={`calendar-month-${ym}`}>
      <div className="bg-zinc-900 text-white px-3 py-2 flex items-center justify-between">
        <div className="font-display font-black text-sm tracking-tight uppercase">
          {MONTHS_FR[monthIdx]} <span className="text-[#FFC60A]">{monthYear}</span>
        </div>
        <div className="text-[10px] font-mono text-zinc-400">
          {filtered.filter((d) => d.status === "work" || d.status === "override_in").length} jours
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-zinc-200 p-px">
        {WEEKDAYS_FR.map((w, i) => (
          <div key={i} className="text-[9px] font-mono uppercase text-zinc-500 text-center py-1 bg-zinc-50">{w}</div>
        ))}
        {Array.from({ length: firstWd }, (_, i) => (
          <div key={`pad-${i}`} className="bg-zinc-50" />
        ))}
        {filtered.map((d) => {
          const s = STATUS_STYLE[d.status] || STATUS_STYLE.work;
          const day = parseInt(d.date.slice(-2), 10);
          const isOverridden = !!overridesByDate[d.date];
          return (
            <button
              key={d.date}
              onClick={() => onCellClick(d)}
              className={`relative ${s.bg} ${s.text} ${s.ring} border aspect-square hover:ring-2 hover:ring-[#FFC60A] transition flex items-center justify-center text-xs font-bold focus:outline-none focus:ring-2 focus:ring-[#FFC60A]`}
              data-testid={`calendar-day-${d.date}`}
              title={`${d.date} — ${s.label}${d.reason ? ` (${d.reason})` : ""}`}
            >
              {day}
              {isOverridden && (
                <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-zinc-900 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-zinc-600" data-testid="calendar-legend">
      {Object.entries(STATUS_STYLE).map(([k, s]) => (
        <span key={k} className="flex items-center gap-1.5">
          <span className={`w-3.5 h-3.5 ${s.bg} ${s.ring} border inline-block`} />
          {s.label}
        </span>
      ))}
      <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-zinc-900 rounded-full" /> Override</span>
    </div>
  );
}

function OverrideModal({ open, defaultDate, defaultKind, currentReason, onClose, onSubmit, onDelete }) {
  const [kind, setKind] = useState(defaultKind || "exclude");
  const [reason, setReason] = useState(currentReason || "");
  useEffect(() => {
    if (open) { setKind(defaultKind || "exclude"); setReason(currentReason || ""); }
  }, [open, defaultKind, currentReason]);
  if (!open) return null;
  const isEdit = Boolean(currentReason !== null && currentReason !== undefined);
  return (
    <div className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4" data-testid="calendar-override-modal">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] w-full max-w-md">
        <div className="bg-zinc-900 text-white px-4 py-3 flex items-center gap-3">
          <CalendarDays size={16} className="text-[#FFC60A]" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">{isEdit ? "Modifier" : "Nouvel override"}</div>
            <div className="font-display font-black text-lg tracking-tighter">{defaultDate}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-700 rounded" data-testid="calendar-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Action</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setKind("exclude")}
                className={`px-3 py-2 text-xs font-bold uppercase border ${kind === "exclude" ? "bg-fuchsia-500 text-white border-fuchsia-700" : "bg-zinc-100 text-zinc-700 border-zinc-300 hover:bg-zinc-200"}`}
                data-testid="calendar-modal-kind-exclude"
              >Fermer ce jour</button>
              <button
                onClick={() => setKind("include")}
                className={`px-3 py-2 text-xs font-bold uppercase border ${kind === "include" ? "bg-emerald-500 text-white border-emerald-700" : "bg-zinc-100 text-zinc-700 border-zinc-300 hover:bg-zinc-200"}`}
                data-testid="calendar-modal-kind-include"
              >Forcer cours</button>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Motif</div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={kind === "exclude" ? "ex: Sortie scolaire, Journée pédagogique" : "ex: Rattrapage, Cours exceptionnel"}
              className="w-full border-2 border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 outline-none"
              data-testid="calendar-modal-reason"
              autoFocus
            />
          </div>
          <div className="text-[11px] text-zinc-600 bg-zinc-50 p-2 border-l-2 border-zinc-900">
            <Info size={11} className="inline mr-1" />
            {kind === "exclude"
              ? "Cette date sera retirée des journées de cours. Les services planifiés ne seront PAS générés pour ce jour lors du prochain import."
              : "Cette date sera ajoutée comme jour de cours, même si c'est un week-end, un férié ou des vacances."}
          </div>
          <div className="flex items-center justify-between pt-2">
            {isEdit && (
              <button
                onClick={onDelete}
                className="text-xs font-bold uppercase text-red-700 hover:underline inline-flex items-center gap-1"
                data-testid="calendar-modal-delete"
              >
                <Trash2 size={12} /> Supprimer l'override
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <button
                onClick={onClose}
                className="px-3 py-2 text-xs font-bold uppercase border border-zinc-300 hover:bg-zinc-100"
                data-testid="calendar-modal-cancel"
              >Annuler</button>
              <button
                onClick={() => onSubmit({ kind, reason })}
                disabled={!reason.trim()}
                className="px-3 py-2 text-xs font-bold uppercase bg-[#FFC60A] text-zinc-900 border border-zinc-900 shadow-[2px_2px_0_0_#0A0A0B] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="calendar-modal-save"
              >Enregistrer</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Édition de l'année scolaire (bornes, vacances, fériés, A/B) ───────── */
function YearSettingsPanel({ year, raw, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_day: raw.first_day || "",
    last_day: raw.last_day || "",
    ab_anchor_date: raw.ab_anchor_date || raw.first_day || "",
    ab_anchor_letter: raw.ab_anchor_letter || "A",
    ab_mode: raw.ab_mode || "civil",
    vacations: (raw.vacations || []).map((v) => ({ ...v })),
    holidays: (raw.holidays || []).map((h) => ({ ...h })),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVac = (i, k, v) => setForm((f) => {
    const vacations = f.vacations.map((x, j) => (j === i ? { ...x, [k]: v } : x));
    return { ...f, vacations };
  });
  const setHol = (i, k, v) => setForm((f) => {
    const holidays = f.holidays.map((x, j) => (j === i ? { ...x, [k]: v } : x));
    return { ...f, holidays };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/calendar/school-year/${year}`, form);
      toast.success(`Année ${year} enregistrée — la génération utilisera ces dates`);
      onSaved();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  const inp = "border border-zinc-300 px-2 py-1.5 text-sm bg-white";
  return (
    <div className="mb-4 bg-white border-2 border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]" data-testid="year-settings-panel">
      <button onClick={() => setOpen((v) => !v)} data-testid="year-settings-toggle"
        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-50 text-left">
        <Sparkles size={15} className="text-[#FFC60A]" />
        <div className="flex-1">
          <span className="font-display font-black text-sm uppercase tracking-wider">Paramètres de l'année {year}</span>
          <span className="text-xs text-zinc-500 ml-3">
            Rentrée : <strong>{form.first_day}</strong> · Fin : <strong>{form.last_day}</strong> · {form.vacations.length} période(s) de vacances · {form.holidays.length} férié(s) · Semaine {form.ab_anchor_letter} dès le {form.ab_anchor_date} ({form.ab_mode === "civil" ? "alternance civile" : "alternance scolaire"})
          </span>
        </div>
        <ChevronDown size={14} className={open ? "rotate-180" : ""} />
      </button>
      {open && (
        <div className="border-t border-zinc-200 p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <label className="text-xs">
              <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Rentrée (début)</span>
              <input type="date" value={form.first_day} onChange={(e) => set("first_day", e.target.value)} className={inp + " w-full"} data-testid="year-first-day" />
            </label>
            <label className="text-xs">
              <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Fin d'année</span>
              <input type="date" value={form.last_day} onChange={(e) => set("last_day", e.target.value)} className={inp + " w-full"} data-testid="year-last-day" />
            </label>
            <label className="text-xs">
              <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Ancrage A/B (date)</span>
              <input type="date" value={form.ab_anchor_date} onChange={(e) => set("ab_anchor_date", e.target.value)} className={inp + " w-full"} data-testid="year-ab-anchor-date" />
            </label>
            <label className="text-xs">
              <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Lettre de départ</span>
              <select value={form.ab_anchor_letter} onChange={(e) => set("ab_anchor_letter", e.target.value)} className={inp + " w-full"} data-testid="year-ab-anchor-letter">
                <option value="A">A</option><option value="B">B</option>
              </select>
            </label>
            <label className="text-xs">
              <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Alternance A/B</span>
              <select value={form.ab_mode} onChange={(e) => set("ab_mode", e.target.value)} className={inp + " w-full"} data-testid="year-ab-mode">
                <option value="civil">Semaines civiles (continue)</option>
                <option value="scolaire">Semaines scolaires uniquement</option>
              </select>
            </label>
          </div>
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
            ⚠ Le mode d'alternance A/B pendant les vacances est une <strong>règle métier MBC à confirmer</strong>. Modifiez-le seulement après validation, puis ré-importez les programmes concernés.
          </p>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">Vacances scolaires</div>
            <div className="space-y-1.5">
              {form.vacations.map((v, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`year-vacation-row-${i}`}>
                  <input type="date" value={v.start} onChange={(e) => setVac(i, "start", e.target.value)} className={inp} />
                  <span className="text-zinc-400 text-xs">→</span>
                  <input type="date" value={v.end} onChange={(e) => setVac(i, "end", e.target.value)} className={inp} />
                  <input type="text" value={v.label} onChange={(e) => setVac(i, "label", e.target.value)} placeholder="Libellé" className={inp + " flex-1"} />
                  <button onClick={() => set("vacations", form.vacations.filter((_, j) => j !== i))} className="p-1.5 text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200" title="Supprimer" data-testid={`year-vacation-delete-${i}`}>✕</button>
                </div>
              ))}
            </div>
            <button onClick={() => set("vacations", [...form.vacations, { start: "", end: "", label: "" }])}
              className="mt-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900" data-testid="year-vacation-add">+ Ajouter une période</button>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">Jours fériés</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {form.holidays.map((h, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`year-holiday-row-${i}`}>
                  <input type="date" value={h.date} onChange={(e) => setHol(i, "date", e.target.value)} className={inp} />
                  <input type="text" value={h.label} onChange={(e) => setHol(i, "label", e.target.value)} placeholder="Libellé" className={inp + " flex-1"} />
                  <button onClick={() => set("holidays", form.holidays.filter((_, j) => j !== i))} className="p-1.5 text-red-600 hover:bg-red-50" title="Supprimer" data-testid={`year-holiday-delete-${i}`}>✕</button>
                </div>
              ))}
            </div>
            <button onClick={() => set("holidays", [...form.holidays, { date: "", label: "" }])}
              className="mt-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900" data-testid="year-holiday-add">+ Ajouter un férié</button>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setOpen(false)} className="px-3 py-2 border border-zinc-300 text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-100">Annuler</button>
            <button onClick={save} disabled={saving} data-testid="year-settings-save"
              className="px-4 py-2 bg-zinc-900 text-[#FFC60A] text-[11px] font-black uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : null} Enregistrer l'année
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CalendarPage() {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, date: null, kind: null, reason: null });
  const [showCopy, setShowCopy] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);

  const fetchYears = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar/school-years");
      const list = data?.years || [];
      setYears(list);
      if (list.length && !year) {
        const today = new Date().toISOString().slice(0, 10);
        const active = list.find((y) => y.first_day <= today && today <= y.last_day);
        setYear((active || list[0]).year);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    }
  }, [year]);

  const fetchYear = useCallback(async (y) => {
    if (!y) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/calendar/school-year/${y}/computed`);
      const { data: raw } = await api.get(`/calendar/school-year/${y}`);
      setData({ ...data, overrides: raw.overrides || [], raw });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchYears(); }, [fetchYears]);
  useEffect(() => { fetchYear(year); }, [year, fetchYear]);

  const overridesByDate = useMemo(() => {
    const idx = {};
    (data?.overrides || []).forEach((o) => { idx[o.date] = o; });
    return idx;
  }, [data]);

  // Group days by YYYY-MM for the grid
  const monthBuckets = useMemo(() => {
    const buckets = new Map();
    (data?.days || []).forEach((d) => {
      const k = ymKey(d.date);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(d);
    });
    return Array.from(buckets.keys()).sort();
  }, [data]);

  const onCellClick = (d) => {
    const ov = overridesByDate[d.date];
    setModal({
      open: true,
      date: d.date,
      kind: ov ? ov.kind : (d.status === "work" || d.status === "override_in" ? "exclude" : "include"),
      reason: ov ? (ov.reason || "") : "",
    });
  };

  const submitOverride = async ({ kind, reason }) => {
    try {
      await api.post(`/calendar/school-year/${year}/override`, {
        date: modal.date, kind, reason,
      });
      toast.success(`Override ${kind === "exclude" ? "fermé" : "ouvert"} enregistré pour ${modal.date}`);
      setModal({ open: false, date: null, kind: null, reason: null });
      fetchYear(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    }
  };

  const deleteOverride = async () => {
    try {
      await api.delete(`/calendar/school-year/${year}/override?date_iso=${modal.date}`);
      toast.success("Override supprimé");
      setModal({ open: false, date: null, kind: null, reason: null });
      fetchYear(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    }
  };

  const copyFromYear = async (src) => {
    if (src === year) return;
    setCopyBusy(true);
    try {
      const { data } = await api.post(`/calendar/school-year/${year}/copy-from/${src}`);
      toast.success(`${data.copied || 0} override(s) copié(s) depuis ${src}`);
      setShowCopy(false);
      fetchYear(year);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur");
    } finally {
      setCopyBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3rem)] bg-zinc-100" data-testid="admin-calendar-page">
      {/* Header */}
      <div className="bg-zinc-900 text-white px-6 py-4 border-b-2 border-[#FFC60A]">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <GraduationCap size={22} className="text-[#FFC60A]" />
              <h1 className="font-display font-black text-2xl tracking-tight leading-none">Calendrier scolaire</h1>
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-400 mt-1">Vacances · Fériés · Overrides</span>
            </div>
            <div className="text-zinc-300 mt-1 text-sm max-w-2xl">
              Définissez ici les <strong>fermetures exceptionnelles</strong> (sorties scolaires, journées pédagogiques)
              et les <strong>rattrapages</strong>. Les imports Excel ne génèrent pas de services pour les jours fermés.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm font-mono"
              data-testid="calendar-year-select"
            >
              {years.map((y) => (
                <option key={y.year} value={y.year}>{y.year}</option>
              ))}
            </select>
            <div className="relative">
              <button
                onClick={() => setShowCopy((v) => !v)}
                className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1"
                data-testid="calendar-copy-open"
              >
                <Sparkles size={13} /> Copier depuis…
                <ChevronDown size={12} />
              </button>
              {showCopy && (
                <div className="absolute right-0 mt-1 bg-white border-2 border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B] z-50 min-w-[160px]">
                  {years.filter((y) => y.year !== year).map((y) => (
                    <button
                      key={y.year}
                      onClick={() => copyFromYear(y.year)}
                      disabled={copyBusy}
                      className="block w-full text-left px-3 py-2 text-xs font-mono uppercase hover:bg-[#FFC60A] disabled:opacity-50"
                      data-testid={`calendar-copy-${y.year}`}
                    >
                      {copyBusy ? <Loader2 size={11} className="inline animate-spin" /> : null} {y.year}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => fetchYear(year)}
              disabled={loading}
              className="p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"
              data-testid="calendar-refresh"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {data && (
            <>
              <div className="flex flex-col px-3 py-2 min-w-[88px] bg-emerald-600 text-white border border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]" data-testid="calendar-kpi-working">
                <span className="text-[9px] font-mono uppercase tracking-[0.2em] opacity-80">Jours de cours</span>
                <span className="text-2xl font-display font-black leading-none mt-0.5">{data.working_count}</span>
              </div>
              <div className="flex flex-col px-3 py-2 min-w-[88px] bg-zinc-700 text-white border border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]" data-testid="calendar-kpi-excluded">
                <span className="text-[9px] font-mono uppercase tracking-[0.2em] opacity-80">Jours exclus</span>
                <span className="text-2xl font-display font-black leading-none mt-0.5">{data.excluded_count}</span>
              </div>
              <div className="flex flex-col px-3 py-2 min-w-[88px] bg-[#FFC60A] text-zinc-900 border border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]" data-testid="calendar-kpi-overrides">
                <span className="text-[9px] font-mono uppercase tracking-[0.2em] opacity-80">Overrides</span>
                <span className="text-2xl font-display font-black leading-none mt-0.5">{data.overrides_count}</span>
              </div>
              <div className="text-zinc-400 text-xs ml-2 self-end">
                Du <strong className="text-white">{data.first_day}</strong> au <strong className="text-white">{data.last_day}</strong>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        {data?.raw && (
          <YearSettingsPanel key={year} year={year} raw={data.raw} onSaved={() => fetchYear(year)} />
        )}
        <div className="mb-4">
          <Legend />
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-[#FFC60A]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="calendar-months-grid">
            {monthBuckets.map((ym) => (
              <MonthGrid
                key={ym}
                year={year}
                ym={ym}
                days={data?.days || []}
                overridesByDate={overridesByDate}
                onCellClick={onCellClick}
              />
            ))}
          </div>
        )}

        {/* Overrides list */}
        {(data?.overrides || []).length > 0 && (
          <div className="mt-8 bg-white border-2 border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]" data-testid="calendar-overrides-list">
            <div className="bg-zinc-900 text-white px-4 py-2 font-display font-bold text-sm uppercase tracking-wider">
              Overrides actifs ({data.overrides.length})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] font-mono uppercase text-zinc-500">
                  <th className="text-left px-3 py-1.5">Date</th>
                  <th className="text-left px-3 py-1.5">Type</th>
                  <th className="text-left px-3 py-1.5">Motif</th>
                  <th className="text-right px-3 py-1.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {[...data.overrides].sort((a, b) => a.date.localeCompare(b.date)).map((o) => (
                  <tr key={o.date} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2 font-mono">{o.date}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${o.kind === "include" ? "bg-emerald-100 text-emerald-900" : "bg-fuchsia-100 text-fuchsia-900"}`}>
                        {o.kind === "include" ? "Cours" : "Fermé"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-700">{o.reason || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => onCellClick({ date: o.date, status: o.kind === "include" ? "override_in" : "override_out" })}
                        className="text-xs underline text-zinc-700 hover:text-zinc-900"
                        data-testid={`calendar-override-edit-${o.date}`}
                      >Modifier</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OverrideModal
        open={modal.open}
        defaultDate={modal.date}
        defaultKind={modal.kind}
        currentReason={modal.reason}
        onClose={() => setModal({ open: false, date: null, kind: null, reason: null })}
        onSubmit={submitOverride}
        onDelete={deleteOverride}
      />
    </div>
  );
}
